import { useCallback, useState, useEffect } from "react";
import { TellescopeModelFromName, useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { toast } from "sonner";
import { usePDFGenerator, UsePDFGeneratorOptions } from "./usePDFGenerator";

/**
 * Configuration for PDF actions
 */
export interface PDFActionsConfig extends UsePDFGeneratorOptions {
  /**
   *
   */
  filename?: string;
  /**
   * Contact/enduser ID to associate the uploaded file with
   */
  enduserId: string;

  /**
   * Optional logo URL to convert to data URL for PDF generation
   * This avoids CORS issues with external images
   */
  logoUrl?: string;

  /**
   * Whether the uploaded file should be hidden from the enduser
   * @default false
   */
  hiddenFromEnduser?: boolean;

  /**
   * Whether to flag the uploaded file for review
   * @default false
   */
  flagForReview?: boolean;

  /**
   * Callback invoked when PDF is successfully uploaded
   */
  onUploadSuccess?: (fileId: string) => void;

  /**
   * Callback invoked when PDF generation or upload fails
   */
  onUploadError?: (error: Error) => void;

  /**
   * Callback for navigation after "Save and Share" action
   * Should navigate the user to the appropriate sharing destination (inbox, etc.)
   */
  onShareNavigate?: (fileSecureName: string) => void;

  /**
   * Custom success message for upload completion
   */
  uploadSuccessMessage?: string;

  /**
   * Custom error message for upload failure
   */
  uploadErrorMessage?: string;
}

/**
 * Return type for usePDFActions hook
 */
export interface UsePDFActionsReturn {
  /**
   * Generate PDF and trigger browser download
   * Uses the browser's built-in download mechanism
   */
  generateAndDownload: () => Promise<void>;

  /**
   * Generate PDF and upload to the contact/enduser profile
   */
  generateAndSaveToProfile: () => Promise<TellescopeModelFromName<"files"> | undefined>;

  /**
   * Generate PDF, upload to profile, and navigate to sharing destination
   */
  generateAndShare: () => Promise<void>;

  /**
   * Whether PDF is currently being generated
   */
  isGenerating: boolean;

  /**
   * Whether PDF is currently being uploaded
   */
  isUploading: boolean;

  /**
   * Whether any action is in progress
   */
  isBusy: boolean;

  /**
   * Error from PDF generation or upload, if any
   */
  error: Error | null;

  /**
   * Portal component for rendering PDF content
   * Content is rendered off-screen for PDF generation
   */
  PDFPortal: React.FC<{ children: React.ReactNode }>;

  /**
   * Logo converted to data URL (if logoUrl was provided)
   * Pass this to FormResponsePrintablePDF to avoid CORS issues
   */
  logoDataUrl?: string;
}

/**
 * Generic hook for PDF generation actions (download, save to profile, share).
 *
 * Combines PDF generation with file upload functionality, providing three
 * common actions for working with PDFs in the application.
 *
 * @param config - Configuration for PDF actions including upload destination and callbacks
 * @returns Object with action methods, loading states, and PDFPortal component
 *
 * @example
 * Basic usage for form responses:
 * ```tsx
 * function FormResponseCard({ formResponse, contactId }) {
 *   const pdfActions = usePDFActions({
 *     filename: `form-response-${formResponse.id}`,
 *     enduserId: contactId,
 *     onUploadSuccess: (fileId) => {
 *       console.log("PDF uploaded:", fileId);
 *     },
 *     onShareNavigate: () => {
 *       navigate("/inbox");
 *     },
 *   });
 *
 *   return (
 *     <>
 *       <Button
 *         onClick={pdfActions.generateAndSaveToProfile}
 *         disabled={pdfActions.isBusy}
 *       >
 *         Save to Profile
 *       </Button>
 *
 *       <pdfActions.PDFPortal>
 *         <FormResponsePrintablePDF formResponse={formResponse} />
 *       </pdfActions.PDFPortal>
 *     </>
 *   );
 * }
 * ```
 *
 * @example
 * With custom messages:
 * ```tsx
 * const pdfActions = usePDFActions({
 *   filename: "patient-report",
 *   enduserId: patientId,
 *   uploadSuccessMessage: "Report saved to patient profile",
 *   uploadErrorMessage: "Failed to save report",
 *   onUploadSuccess: () => {
 *     // Refresh data
 *   },
 * });
 * ```
 *
 * @example
 * Reusable across different document types:
 * ```tsx
 * // For medical reports
 * const reportActions = usePDFActions({
 *   filename: "medical-report",
 *   enduserId: patientId,
 *   onShareNavigate: () => navigate("/share-report"),
 * });
 *
 * // For invoices
 * const invoiceActions = usePDFActions({
 *   filename: "invoice",
 *   enduserId: clientId,
 *   onShareNavigate: () => navigate("/send-invoice"),
 * });
 * ```
 */
export function usePDFActions(config: PDFActionsConfig): UsePDFActionsReturn {
  const {
    enduserId,
    hiddenFromEnduser = false,
    flagForReview = false,
    onUploadSuccess,
    onUploadError,
    onShareNavigate,
    uploadSuccessMessage = "PDF saved successfully",
    uploadErrorMessage = "Failed to save PDF",
    filename = "document",
    logoUrl,
    ...pdfGeneratorOptions
  } = config;

  // State for logo data URL
  const [logoDataUrl, setLogoDataUrl] = useState<string>();

  // Fetch logo and convert to data URL to avoid CORS issues
  useEffect(() => {
    if (!logoUrl) {
      return;
    }

    const fetchAndConvertLogo = async () => {
      try {
        // Fetch the image using fetch (no CORS for initial fetch)
        const response = await fetch(logoUrl, { mode: "no-cors" });
        const blob = await response.blob();

        // Create object URL from blob
        const objectUrl = URL.createObjectURL(blob);

        // Load image from object URL
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            resolve(null);
          };
          img.onerror = (error) => {
            console.error("Logo failed to load from object URL:", error);
            reject(error);
          };
          img.src = objectUrl;
        });

        // Convert to canvas then data URL
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          setLogoDataUrl(dataUrl);
        }

        // Clean up object URL
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Failed to fetch and convert logo:", error);
        // Don't set fallback - let the component skip the logo
      }
    };

    fetchAndConvertLogo();
  }, [logoUrl]);

  // PDF generation hook
  const { generatePDF, isGenerating, error: pdfError, PDFPortal } = usePDFGenerator(pdfGeneratorOptions);

  // File upload mutation
  const uploadFileMutation = useUploadTellescopeFile({
    prepareMutationOptions: {
      onError: (error) => {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        toast.error(uploadErrorMessage);
        onUploadError?.(errorObj);
      },
    },
    confirmMutationOptions: {
      onSuccess: () => {
        toast.success(uploadSuccessMessage);
      },
      onError: (error) => {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        toast.error(uploadErrorMessage);
        onUploadError?.(errorObj);
      },
    },
  });

  const isUploading = uploadFileMutation.stage !== "idle" && uploadFileMutation.stage !== "success";
  const isBusy = isGenerating || isUploading;
  const uploadError =
    uploadFileMutation.prepareError || uploadFileMutation.uploadError || uploadFileMutation.confirmError;
  const error = pdfError || uploadError;

  /**
   * Generate PDF and trigger download in browser
   */
  const generateAndDownload = useCallback(async () => {
    try {
      const blob = await generatePDF();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("PDF downloaded");
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      toast.error("Failed to generate PDF");
      onUploadError?.(errorObj);
    }
  }, [generatePDF, filename, onUploadError]);

  /**
   * Generate PDF and upload to profile
   */
  const generateAndSaveToProfile = useCallback(async () => {
    try {
      const blob = await generatePDF();

      // Upload the PDF blob
      const fileRecord = await uploadFileMutation.uploadFileAsync(
        {
          name: `${filename}.pdf`,
          size: blob.size,
          type: "application/pdf",
          publicRead: false,
          isCalledOut: flagForReview,
          enduserId,
          hiddenFromEnduser,
        },
        blob
      );

      // Call success callback with file ID
      onUploadSuccess?.(fileRecord?.id);
      return fileRecord;
    } catch (error) {
      // Error handling is done in mutation callbacks
      console.error("Failed to save PDF to profile:", error);
    }
  }, [generatePDF, filename, flagForReview, enduserId, hiddenFromEnduser, uploadFileMutation, onUploadSuccess]);

  /**
   * Generate PDF, upload to profile, and navigate to sharing destination
   */
  const generateAndShare = useCallback(async () => {
    try {
      const file = await generateAndSaveToProfile();

      // Navigate after successful upload
      if (onShareNavigate && file?.secureName) {
        onShareNavigate(file?.secureName);
      }
    } catch (error) {
      // Error handling is done in generateAndSaveToProfile
      console.error("Failed to share PDF:", error);
    }
  }, [generateAndSaveToProfile, onShareNavigate]);

  return {
    generateAndDownload,
    generateAndSaveToProfile,
    generateAndShare,
    isGenerating,
    isUploading,
    isBusy,
    error,
    PDFPortal,
    logoDataUrl,
  };
}
